import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { 
  Building2,
  Scissors,
  BookOpen,
  Layers,
  Tag,
  RefreshCcw,
  Check,
  ChevronsUpDown,
  ArrowLeft,
  Save,
  Sparkles,
  Globe as GlobeIcon,
  Clock
} from 'lucide-react';
import { cn } from "@/lib/utils";
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../../firebase/config';
import { collection, addDoc, serverTimestamp, query, orderBy, onSnapshot } from 'firebase/firestore';
import { toast } from 'sonner';
import { subjectCategories } from './subjectCategories';



const AddCourseView = () => {
  const navigate = useNavigate();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    levels: '',
    category: '',
    isMultiSchool: 'no',
    isContinuation: false,
    continuesFrom: '',
    customCategory: '',
    schools: [''],
    approxHours: ''
  });
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [openSearch, setOpenSearch] = useState(false);
  const [openCategory, setOpenCategory] = useState(false);
  const [existingSubjects, setExistingSubjects] = useState<{value: string, label: string}[]>([]);

  useEffect(() => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const q = query(collection(db, `teaching_workspaces/${uid}/courses`), orderBy('createdAt', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetched = snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          value: doc.id,
          label: `${data.title} ${data.levels ? `(Level ${data.levels})` : ''}`.trim()
        };
      });
      setExistingSubjects(fetched);
    });

    return () => unsubscribe();
  }, []);



  const handleAddSchool = () => {
    setFormData({ ...formData, schools: [...formData.schools, ''] });
  };

  const handleRemoveSchool = (index: number) => {
    const newSchools = formData.schools.filter((_, i) => i !== index);
    setFormData({ ...formData, schools: newSchools.length ? newSchools : [''] });
  };

  const handleSchoolChange = (index: number, value: string) => {
    const newSchools = [...formData.schools];
    newSchools[index] = value;
    setFormData({ ...formData, schools: newSchools });
  };

  // Validation function
  const validateFields = () => {
    const missing: string[] = [];
    if (!formData.title.trim()) missing.push('Title');
    if (!formData.category) missing.push('Category');
    if (formData.category === 'other' && !formData.customCategory.trim()) missing.push('Custom Category');
    if (!formData.approxHours.trim()) missing.push('Syllabus Hours');
    if (formData.isMultiSchool === 'yes' && formData.schools.filter(s => s.trim() !== '').length === 0) missing.push('Assigned Schools');
    if (formData.isContinuation && !formData.continuesFrom) missing.push('Continues From');
    return missing;
  };

  // Check if form is valid
  const isFormValid = () => {
    return validateFields().length === 0;
  };

  const handleSave = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!auth.currentUser) {
      toast.error('You must be logged in to create a course.');
      return;
    }

    const missing = validateFields();
    setMissingFields(missing);
    if (missing.length > 0) {
      missing.forEach(field => toast.error(`Please fill the ${field}.`));
      return;
    }

    setIsSaving(true);
    try {
      const uid = auth.currentUser.uid;
      const coursesRef = collection(db, `teaching_workspaces/${uid}/courses`);
      await addDoc(coursesRef, {
        title: formData.title.trim(),
        levels: formData.levels,
        category: formData.category,
        customCategory: formData.customCategory,
        approxHours: formData.approxHours,
        isMultiSchool: formData.isMultiSchool === 'yes',
        schools: formData.isMultiSchool === 'yes' ? formData.schools.filter(s => s.trim() !== '') : [],
        isContinuation: formData.isContinuation,
        continuesFrom: formData.isContinuation ? formData.continuesFrom : '',
        createdAt: serverTimestamp(),
      });
      toast.success("Subject successfully created!");
      navigate('/dashboard/lessons');
    } catch(err: any) {
      console.error(err);
      toast.error(err.message || "Failed to save subject.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, y: 15 }} 
      animate={{ opacity: 1, y: 0 }} 
      className="max-w-2xl mx-auto space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate(-1)} 
            className="rounded-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-white">
              Add New Subject/Course
            </h1>
            <p className="text-sm text-muted-foreground font-medium">
              Initialize a new curriculum entry.
            </p>
          </div>
        </div>
      </div>

      <Card className="border border-slate-200 dark:border-slate-800 shadow-xl bg-white dark:bg-slate-950/40 backdrop-blur-md overflow-hidden">
        <motion.div
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
        >
          <CardHeader className="border-b border-slate-100 dark:border-slate-800/50 pb-6">
            <CardTitle className="text-lg font-bold flex items-center gap-2 text-indigo-500">
              <BookOpen className="h-5 w-5" />
              Course Configuration
            </CardTitle>
            <CardDescription className="text-xs">Define the core properties of this subject.</CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSave} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="title" className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                      <BookOpen className="h-3 w-3" /> Subject/Course Title
                    </Label>
                    <Input 
                      id="title"
                      placeholder="e.g. Mathematics, Science"
                      className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 md:col-span-2">
                    <div className="space-y-2">
                      <Label htmlFor="levels" className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                        <Layers className="h-3 w-3" /> Levels <span className="text-[10px] lowercase font-normal opacity-70">(optional)</span>
                      </Label>
                      <Input 
                        id="levels"
                        type="number"
                        placeholder="0"
                        className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm"
                        value={formData.levels}
                        onChange={(e) => setFormData({...formData, levels: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="approxHours" className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                        <Clock className="h-3 w-3" /> Syllabus Hours
                      </Label>
                      <Input 
                        id="approxHours"
                        type="number"
                        placeholder="e.g. 40"
                        className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm"
                        value={formData.approxHours}
                        onChange={(e) => setFormData({...formData, approxHours: e.target.value})}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                      <Tag className="h-3 w-3" /> Category
                    </Label>
                    <Popover open={openCategory} onOpenChange={setOpenCategory}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          role="combobox"
                          aria-expanded={openCategory}
                          className="w-full justify-between bg-white dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 rounded-lg text-sm font-normal text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900/80"
                        >
                          {formData.category ? (
                            <div className="flex items-center gap-2">
                              {(() => {
                                const cat = subjectCategories.find(c => c.value === formData.category);
                                if (cat) {
                                  const Icon = cat.icon;
                                  return (
                                    <div className={cn("p-1.5 rounded-md flex items-center justify-center border", cat.bg, cat.border)}>
                                      <Icon className={cn("h-4 w-4", cat.color)} />
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              <span className="text-slate-900 dark:text-slate-100 font-bold ml-1">
                                {formData.category === 'other' && formData.customCategory 
                                  ? formData.customCategory 
                                  : subjectCategories.find((c) => c.value === formData.category)?.label}
                              </span>
                            </div>
                          ) : (
                            "Select category..."
                          )}
                          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl rounded-xl overflow-hidden">
                        <Command className="bg-transparent">
                          <CommandInput 
                            placeholder="Search categories..." 
                            className="h-10 border-none focus:ring-0 text-sm text-slate-900 dark:text-slate-100" 
                          />
                          <CommandList className="max-h-[300px] overflow-y-auto">
                            <CommandEmpty className="py-4 text-xs text-muted-foreground uppercase font-bold tracking-tighter italic text-center">No category found</CommandEmpty>
                            <CommandGroup className="p-1">
                              {subjectCategories.map((category) => (
                                <CommandItem
                                  key={category.value}
                                  value={category.value}
                                  onSelect={(currentValue) => {
                                    setFormData({...formData, category: currentValue})
                                    setOpenCategory(false)
                                  }}
                                  className="flex items-center gap-3 py-2.5 px-3 mb-1 rounded-lg cursor-pointer data-[selected=true]:bg-slate-50 dark:data-[selected=true]:bg-slate-800/50 text-slate-700 dark:text-slate-300 text-sm transition-all group/item"
                                >
                                  <div className={cn(
                                    "p-2 rounded-lg flex items-center justify-center border shadow-sm transition-all group-data-[selected=true]/item:scale-110",
                                    category.bg,
                                    category.border
                                  )}>
                                    <category.icon className={cn("h-4 w-4", category.color)} />
                                  </div>
                                  <span className="flex-1 font-bold tracking-tight">{category.label}</span>
                                  <Check
                                    className={cn(
                                      "h-4 w-4 text-indigo-500 transition-opacity",
                                      formData.category === category.value ? "opacity-100" : "opacity-0"
                                    )}
                                  />
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </CommandList>
                        </Command>
                      </PopoverContent>
                    </Popover>

                    {formData.category === 'other' && (
                      <motion.div 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mt-2 space-y-2 animate-in fade-in slide-in-from-top-2"
                      >
                        <Label htmlFor="customCategory" className="text-[10px] font-bold uppercase text-slate-500">
                          Custom Category Name
                        </Label>
                        <Input 
                          id="customCategory"
                          placeholder="Enter subject type..."
                          className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm h-10"
                          value={formData.customCategory}
                          onChange={(e) => setFormData({...formData, customCategory: e.target.value})}
                          required
                        />
                      </motion.div>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                      <GlobeIcon className="h-3 w-3" /> Multi-School
                    </Label>
                    <Select 
                      value={formData.isMultiSchool} 
                      onValueChange={(v) => setFormData({...formData, isMultiSchool: v})}
                    >
                      <SelectTrigger className="bg-white dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 rounded-lg text-sm transition-all focus:ring-2 focus:ring-indigo-500 ring-offset-white dark:ring-offset-slate-950">
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 text-slate-900 dark:text-slate-100 shadow-xl rounded-xl overflow-hidden">
                        <SelectItem value="no" className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer transition-colors">No</SelectItem>
                        <SelectItem value="yes" className="hover:bg-slate-100 dark:hover:bg-slate-800 focus:bg-slate-100 dark:focus:bg-slate-800 cursor-pointer transition-colors">Yes</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-4 md:col-span-2">
                    <div className="flex items-center space-x-3 p-4 bg-slate-50 dark:bg-slate-900/50 rounded-lg border border-slate-200/50 dark:border-slate-800/50">
                      <Checkbox 
                        id="continuation" 
                        checked={formData.isContinuation}
                        onCheckedChange={(checked) => setFormData({...formData, isContinuation: !!checked})}
                        className="border-slate-400 dark:border-slate-600 data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                      />
                      <div className="grid gap-1.5 leading-none">
                        <label
                          htmlFor="continuation"
                          className="text-xs font-bold leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 flex items-center gap-2"
                        >
                          <RefreshCcw className="h-3 w-3 text-slate-500" /> Subject Continuation
                        </label>
                        <p className="text-[10px] text-muted-foreground uppercase tracking-tight">Recurring curriculum</p>
                      </div>
                    </div>

                    {formData.isContinuation && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="space-y-2 animate-in fade-in slide-in-from-top-2"
                      >
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                          Continues From
                        </Label>
                        
                        <Popover open={openSearch} onOpenChange={setOpenSearch}>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              role="combobox"
                              aria-expanded={openSearch}
                              className="w-full justify-between bg-white dark:bg-slate-950/40 border-slate-200 dark:border-slate-800 rounded-lg text-sm font-normal text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-900/80"
                            >
                              {formData.continuesFrom
                                ? existingSubjects.find((s) => s.value === formData.continuesFrom)?.label
                                : "Select preceding subject..."}
                              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0 bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 shadow-xl rounded-xl overflow-hidden">
                            <Command className="bg-transparent">
                              <CommandInput 
                                placeholder="Search subjects..." 
                                className="h-10 border-none focus:ring-0 text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400" 
                              />
                              <CommandList className="max-h-[300px] overflow-y-auto">
                                <CommandEmpty className="py-4 text-xs text-muted-foreground uppercase font-bold tracking-tighter italic text-center">
                                  {existingSubjects.length === 0 ? "None available" : "No match found"}
                                </CommandEmpty>
                                <CommandGroup className="p-1">
                                  {existingSubjects.map((s) => (
                                    <CommandItem
                                      key={s.value}
                                      value={s.value}
                                      onSelect={(currentValue) => {
                                        setFormData({...formData, continuesFrom: currentValue === formData.continuesFrom ? "" : currentValue})
                                        setOpenSearch(false)
                                      }}
                                      className="flex items-center justify-between py-2.5 px-3 mb-0.5 rounded-lg cursor-pointer data-[selected=true]:bg-slate-100 dark:data-[selected=true]:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm transition-colors group/item"
                                    >
                                      <span className="flex-1 font-medium">{s.label}</span>
                                      <Check
                                        className={cn(
                                          "ml-2 h-4 w-4 text-indigo-500 transition-opacity",
                                          formData.continuesFrom === s.value ? "opacity-100" : "opacity-0"
                                        )}
                                      />
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                      </motion.div>
                    )}
                    
                    {formData.isMultiSchool === 'yes' && (
                      <motion.div 
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="space-y-4 pt-4 border-t border-slate-100 dark:border-slate-800/50 md:col-span-2 overflow-hidden"
                      >
                        <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
                          <Building2 className="h-3 w-3" /> Assigned Schools
                        </Label>
                        
                        {formData.schools.map((school, index) => (
                          <div key={index} className="flex items-end gap-3">
                            <div className="flex-1 space-y-2">
                              <div className="relative group">
                                <Input 
                                  placeholder="Enter school name..."
                                  className="bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 rounded-lg text-sm pr-10 focus:ring-2 focus:ring-indigo-500"
                                  value={school}
                                  onChange={(e) => handleSchoolChange(index, e.target.value)}
                                  required={formData.isMultiSchool === 'yes'}
                                />
                                <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-300 group-focus-within:text-indigo-400 transition-colors" />
                              </div>
                            </div>
                            {formData.schools.length > 1 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => handleRemoveSchool(index)}
                                className="rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20"
                              >
                                <Scissors className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleAddSchool}
                          className="w-full border-dashed border-2 border-slate-200 dark:border-slate-800 py-6 rounded-xl text-slate-500 hover:text-indigo-500 hover:border-indigo-500 transition-all flex items-center gap-2 mt-2"
                        >
                          <div className="p-1 rounded-full bg-slate-100 dark:bg-slate-800 group-hover:bg-indigo-100 transition-colors">
                            <RefreshCcw className="h-4 w-4" />
                          </div>
                          <span>Add Another School</span>
                        </Button>
                      </motion.div>
                    )}

                  </div>
                </div>

                <div className="flex items-center justify-between pt-4 border-t border-slate-100 dark:border-slate-800/50">
                    <Button 
                      type="button" 
                      variant="ghost" 
                      onClick={() => navigate(-1)}
                      className="rounded-lg px-4 text-xs font-bold text-slate-500"
                    >
                      Cancel
                    </Button>

                    <Button
                      type="submit"
                      disabled={isSaving || !isFormValid()}
                      className={
                        cn(
                          "rounded-lg px-6 font-bold transition-all shadow-lg flex items-center gap-2",
                          isFormValid() && !isSaving
                            ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                            : "bg-slate-400 text-white cursor-not-allowed opacity-70"
                        )
                      }
                    >
                      {isSaving ? <Sparkles className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                      Save Subject
                    </Button>
                  </div>
                </form>
              </CardContent>
            </motion.div>
      </Card>
    </motion.div>
  );
};

export default AddCourseView;
